import { Dispatch, SetStateAction, memo } from "react";
import { ICustomFieldDefinition } from "@Clients/CustomFieldsClient";
import { FormInput } from "@components/FormInput/FormInput";
import { Cancel, Delete, Edit, Save } from "@mui/icons-material";
import { Box, Button, IconButton, Typography } from "@mui/material";


type ExistingCustomFieldsProps = {
    customFields: ICustomFieldDefinition[];
    isLoading: boolean;
    isSaving: boolean;
    handleStartEdit: (field: ICustomFieldDefinition) => void;
    handleCancelEdit: () => void;
    handleSaveEdit: () => void;
    handleDeleteField: (id: string) => void;
    editingFieldId: string | null;
    editingField: Partial<ICustomFieldDefinition>;
    setEditingField: Dispatch<SetStateAction<Partial<ICustomFieldDefinition>>>;
}

export const ExistingCustomFields = memo(({
    customFields,
    isLoading,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteField,
    editingFieldId,
    editingField,
    isSaving,
    setEditingField,
}: ExistingCustomFieldsProps) => {
    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Existing Custom Fields ({customFields.length})
            </Typography>
            {isLoading ? (
                <Typography variant="body2" color="text.secondary">
                    Loading...
                </Typography>
            ) : customFields.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No custom fields yet. Add one above to get started.
                </Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {customFields.map((field) => (
                        <Box
                            key={field.id}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 2,
                            }}
                        >
                            {editingFieldId === field.id ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mb: 1, display: "block" }}
                                        >
                                            Field Name (cannot be changed): {field.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mb: 1, display: "block" }}
                                        >
                                            Field Type (cannot be changed): {field.type}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                            gap: 2,
                                        }}
                                    >
                                        <FormInput
                                            label="Field Label"
                                            value={editingField.label || ""}
                                            onChange={(e) =>
                                                setEditingField((prev) => ({
                                                    ...prev,
                                                    label: e.target.value,
                                                }))
                                            }
                                            placeholder="e.g., Job Number"
                                        />
                                        <Box sx={{ display: "flex", alignItems: "center", pt: 2 }}>
                                            <input
                                                type="checkbox"
                                                checked={editingField.required ?? false}
                                                onChange={(e) =>
                                                    setEditingField((prev) => ({
                                                        ...prev,
                                                        required: e.target.checked,
                                                    }))
                                                }
                                                style={{ marginRight: 8 }}
                                            />
                                            <Typography variant="body2">Required field</Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                            gap: 2,
                                        }}
                                    >
                                        <FormInput
                                            label="Placeholder (Optional)"
                                            value={editingField.placeholder || ""}
                                            onChange={(e) =>
                                                setEditingField((prev) => ({
                                                    ...prev,
                                                    placeholder: e.target.value,
                                                }))
                                            }
                                            placeholder="Enter placeholder text"
                                        />
                                        <FormInput
                                            label="Help Text (Optional)"
                                            value={editingField.help_text || ""}
                                            onChange={(e) =>
                                                setEditingField((prev) => ({
                                                    ...prev,
                                                    help_text: e.target.value,
                                                }))
                                            }
                                            placeholder="Tooltip or help text"
                                        />
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Cancel />}
                                            onClick={handleCancelEdit}
                                            size="small"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<Save />}
                                            onClick={handleSaveEdit}
                                            disabled={isSaving}
                                            size="small"
                                        >
                                            {isSaving ? "Saving..." : "Save"}
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body1" fontWeight={500}>
                                            {field.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Key: {field.name} • Type: {field.type}
                                            {field.required && " • Required"}
                                            {field.placeholder && ` • Placeholder: ${field.placeholder}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleStartEdit(field)}
                                            title="Edit"
                                            sx={{ color: "primary.main" }}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteField(field.id)}
                                            title="Delete"
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>)

});
